import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

interface ConfirmationActionProps {
  // Render function for the button (passed as children)
  children: (props: { onClick: () => void }) => ReactNode;

  // Dialog props
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  confirmVariant?: 'text' | 'outlined' | 'contained';
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;

  // Action props
  onConfirm: () => Promise<void> | void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export function ConfirmationAction({
  children,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmColor = 'warning',
  confirmVariant = 'contained',
  maxWidth = 'sm',
  fullWidth = false,
  onConfirm,
  onError,
  onSuccess,
}: ConfirmationActionProps) {
  const { $t } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => {
    setIsOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!isLoading) {
      setIsOpen(false);
      setError(null);
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onConfirm();
      setIsOpen(false);
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err?.message || 'An error occurred';
      setError(errorMessage);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {children({ onClick: handleOpen })}

      <Dialog open={isOpen} onClose={handleClose} maxWidth={maxWidth} fullWidth={fullWidth}>
        {title && <DialogTitle>{title}</DialogTitle>}

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>{message}</Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            {cancelLabel ||
              $t({
                id: 'common.cancel',
                defaultMessage: 'Cancelar',
              })}
          </Button>

          <Button
            onClick={handleConfirm}
            loading={isLoading}
            color={confirmColor}
            variant={confirmVariant}
          >
            {confirmLabel ||
              $t({
                id: 'common.confirm',
                defaultMessage: 'Confirmar',
              })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
